<div
  mat-dialog-title
  class="manual-invoice-dialog-title d-flex align-items-center justify-content-between flex-wrap gap-8"
>
  <ng-container *ngIf="isViewMode && loadingInvoiceData; else invoiceTitleBody">
    <span class="manual-invoice-title-loading f-s-18 f-w-600 m-0">Cargando factura…</span>
  </ng-container>
  <ng-template #invoiceTitleBody>
    <div
      class="manual-invoice-title-body d-flex align-items-center flex-wrap flex-grow-1 min-w-0 gap-10"
      role="status"
      [attr.aria-label]="getInvoiceTitleAriaLabel()"
    >
      <span
        class="manual-invoice-title-type f-s-18 f-w-600 m-0 text-truncate"
        [attr.title]="getDocumentTypeLabel(invoiceForm.get('documentTypeCode')?.value ?? '')"
      >
        {{ getDocumentTypeLabel(invoiceForm.get('documentTypeCode')?.value ?? '') || '—' }}
      </span>
      <div
        class="manual-invoice-title-right d-flex align-items-center flex-wrap gap-10 ms-auto flex-shrink-0 tabular-nums"
      >
        <span
          class="manual-invoice-title-number f-s-18 f-w-600 m-0"
          [class.text-muted]="!getInvoiceNumberDisplay() && !loadingNumber"
        >
          <ng-container *ngIf="loadingNumber; else titleNumberText">
            <mat-spinner diameter="22" class="manual-invoice-title-spinner"></mat-spinner>
          </ng-container>
          <ng-template #titleNumberText>{{ getInvoiceNumberDisplay() || '—' }}</ng-template>
        </span>
        <span class="manual-invoice-title-date f-s-18 f-w-600 m-0">{{
          invoiceForm.get('issueDate')?.value | date:'dd/MM/yyyy'
        }}</span>
      </div>
    </div>
  </ng-template>
  <button type="button" mat-icon-button (click)="onCancel()" matTooltip="Cerrar" aria-label="Cerrar">
    <i-tabler name="x" class="icon-20 d-flex"></i-tabler>
  </button>
</div>

<mat-dialog-content class="manual-invoice-dialog-body">
  <div *ngIf="isViewMode && loadingInvoiceData" class="loading-container">
    <div class="loading-content">
      <mat-spinner diameter="50"></mat-spinner>
      <p class="loading-text m-0">Cargando datos de la factura…</p>
    </div>
  </div>

  <form [formGroup]="invoiceForm" *ngIf="!isViewMode || !loadingInvoiceData">
    <!-- Solo chips de contexto en vista; tipo, número y fecha van solo en el título -->
    <div class="manual-invoice-context-chips m-b-12" *ngIf="data.invoice">
      <div class="d-flex flex-wrap align-items-center gap-8">
        <span
          class="custom-chip"
          [ngClass]="[
            mapChipColor(getStatusColor(data.invoice!.status || '')),
            getStatusLabel(data.invoice!.status) === 'ANULADA' ? 'custom-chip--cancelled' : ''
          ]"
        >
          {{ getStatusLabel(data.invoice!.status) }}
        </span>
        <span
          *ngIf="shouldShowInvoiceTypeChip()"
          class="custom-chip"
          [ngClass]="mapChipColor(getInvoiceTypeColor(data.invoice!.type || data.invoice!.invoiceType || ''))"
        >
          {{ getInvoiceTypeLabel(data.invoice!.type || data.invoice!.invoiceType || '') }}
        </span>
      </div>
      <div *ngIf="data.invoice?.reversed" class="m-t-8">
        <span class="custom-chip custom-chip--warn custom-chip--with-icon">
          <mat-icon>warning</mat-icon>
          Esta factura ha sido revertida por nota de crédito
        </span>
      </div>
    </div>

    <!-- Autorización fiscal (vista) -->
    <div class="invoice-view-section m-b-16" *ngIf="isViewMode && viewInvoiceFiscal">
      <div class="invoice-view-section-title f-s-14 f-w-600 m-b-8">Autorización</div>
      <div class="d-flex flex-wrap gap-16 align-items-start">
        <div>
          <label class="f-s-12 text-muted d-block">Estado</label>
          <span class="f-s-14">{{ getAuthorizationStatusLabel(viewInvoiceFiscal.authorizationStatus) }}</span>
        </div>
        <div *ngIf="viewInvoiceFiscal.authorizationCode">
          <label class="f-s-12 text-muted d-block">CAE</label>
          <span class="f-s-14">{{ viewInvoiceFiscal.authorizationCode }}</span>
        </div>
        <div *ngIf="viewInvoiceFiscal.authorizationExpiration">
          <label class="f-s-12 text-muted d-block">Vencimiento</label>
          <span class="f-s-14">{{ viewInvoiceFiscal.authorizationExpiration | date:'dd/MM/yyyy' }}</span>
        </div>
        <div *ngIf="authorizationQrDataUrl" class="invoice-qr-block">
          <label class="f-s-12 text-muted d-block">QR ARCA</label>
          <img [src]="authorizationQrDataUrl" alt="Código QR de autorización fiscal" width="160" height="160" />
        </div>
      </div>
      <div class="m-t-8" *ngIf="viewInvoiceFiscal.relatedCreditNotes?.length">
        <label class="f-s-12 text-muted d-block">NC vinculadas</label>
        <span class="f-s-14" *ngFor="let nc of viewInvoiceFiscal.relatedCreditNotes; let last = last">
          {{ nc.number }}<span *ngIf="!last">, </span>
        </span>
      </div>
    </div>

    <!-- Comprobante al cliente (impresión) -->
    <div class="invoice-customer-copy m-b-16" *ngIf="isViewMode && viewInvoiceFiscal" id="invoice-print-area">
      <div class="f-s-14 f-w-600 m-b-8">Comprobante al cliente</div>
      <div class="d-flex flex-column align-items-end gap-4" *ngIf="viewInvoiceFiscal.showTaxBreakdownToCustomer === false; else operatorTaxBreakdown">
        <div class="d-flex gap-16">
          <span class="f-s-14 text-muted">Total (IVA incluido)</span>
          <span class="f-s-16 f-w-600">${{ viewInvoiceFiscal.total | number:'1.2-2' }}</span>
        </div>
      </div>
      <ng-template #operatorTaxBreakdown>
        <div class="d-flex flex-column align-items-end gap-4">
          <div class="d-flex gap-16">
            <span class="f-s-13 text-muted">Neto</span>
            <span class="f-s-13">${{ viewInvoiceFiscal.subtotal | number:'1.2-2' }}</span>
          </div>
          <div class="d-flex gap-16">
            <span class="f-s-13 text-muted">IVA</span>
            <span class="f-s-13">${{ viewInvoiceFiscal.taxTotal | number:'1.2-2' }}</span>
          </div>
          <div class="d-flex gap-16">
            <span class="f-s-14 f-w-600">Total</span>
            <span class="f-s-16 f-w-600">${{ viewInvoiceFiscal.total | number:'1.2-2' }}</span>
          </div>
        </div>
      </ng-template>
      <div class="m-t-8" *ngIf="authorizationQrDataUrl">
        <img [src]="authorizationQrDataUrl" alt="QR fiscal" width="120" height="120" />
      </div>
    </div>

    <!-- Cliente (alta): solo Cliente + tipo de factura, sin título de card redundante -->
    <mat-card class="cardWithShadow customer-condition-card m-b-16" *ngIf="!isViewMode">
      <mat-card-content class="customer-condition-content">
        <div class="customer-condition-pair" role="group" aria-label="Cliente y tipo de factura">
          <div class="customer-condition-stack">
            <label
              class="customer-condition-col-label"
              id="manual-invoice-customer-label"
              for="manual-invoice-customer-select"
              >Cliente</label
            >
            <div class="customer-condition-customer">
              <div class="customer-field-wrapper">
                <mat-form-field
                  appearance="outline"
                  class="w-100 customer-condition-field"
                  subscriptSizing="dynamic"
                >
                  <mat-select
                    id="manual-invoice-customer-select"
                    formControlName="selectedCustomer"
                    placeholder="Buscar y seleccionar cliente"
                    [disabled]="customerReadOnly || loadingCustomers"
                    [attr.aria-busy]="loadingCustomers"
                    [attr.aria-labelledby]="'manual-invoice-customer-label'"
                    [attr.aria-describedby]="loadingNumber ? 'customer-hint-loading' : null"
                    (openedChange)="onSelectOpened($event)"
                    (selectionChange)="onCustomerSelected($event)"
                  >
                    <ngx-mat-select-search
                      [formControl]="customerSearchCtrl"
                      [noEntriesFoundLabel]="'No se encontraron clientes'"
                      [placeholderLabel]="'Buscar cliente…'"
                    ></ngx-mat-select-search>
                    <mat-option *ngFor="let customer of filteredCustomers" [value]="customer">
                      <div class="customer-option">
                        <strong>{{ customer.firstName }} {{ customer.surname }}</strong>
                        <small>{{ customer.docType }}: {{ customer.docNumber }}</small>
                      </div>
                    </mat-option>
                  </mat-select>
                  <mat-hint *ngIf="loadingNumber" id="customer-hint-loading"
                    >Obteniendo número de comprobante…</mat-hint
                  >
                  <mat-error *ngIf="invoiceForm.get('selectedCustomer')?.hasError('required')">
                    Debés seleccionar un cliente
                  </mat-error>
                </mat-form-field>
                <div class="customer-loading-overlay" *ngIf="loadingCustomers">
                  <mat-spinner diameter="32"></mat-spinner>
                  <span class="loading-label">Cargando clientes…</span>
                </div>
              </div>
            </div>
          </div>

          <div class="customer-condition-stack customer-condition-stack--compact">
            <span class="customer-condition-col-label" id="invoice-type-label">Tipo de factura</span>
            <div class="customer-condition-type">
              <mat-radio-group
                formControlName="invoiceType"
                class="customer-condition-radio-group"
                aria-labelledby="invoice-type-label"
              >
                <mat-radio-button value="CASH">Contado (pago inmediato)</mat-radio-button>
                <mat-radio-button value="CURRENT_ACCOUNT">Cuenta corriente</mat-radio-button>
              </mat-radio-group>
            </div>
          </div>
        </div>
      </mat-card-content>
    </mat-card>

    <!-- Cliente (vista) -->
    <div class="invoice-view-meta" *ngIf="isViewMode">
      <span class="invoice-view-customer">
        {{ getCustomerDisplayName() }} · Doc. {{ getCustomerDocument() }}
      </span>
    </div>

    <!-- Líneas -->
    <mat-card class="cardWithShadow m-b-16" *ngIf="!isViewMode">
      <mat-card-content class="p-t-8 p-b-4">
        <app-fiscal-document-lines
          [items]="fiscalLines"
          [isViewMode]="isViewMode"
          [itemsCount]="items.length"
          (addItem)="openAddItemDialog()"
          (editItem)="openEditItemDialog($event)"
          (removeItem)="removeItem($event)"
        ></app-fiscal-document-lines>
      </mat-card-content>
    </mat-card>
    <div class="invoice-view-section invoice-view-section--lines" *ngIf="isViewMode">
      <app-fiscal-document-lines
        [items]="fiscalLines"
        [isViewMode]="isViewMode"
        [itemsCount]="items.length"
        (addItem)="openAddItemDialog()"
        (editItem)="openEditItemDialog($event)"
        (removeItem)="removeItem($event)"
      ></app-fiscal-document-lines>
    </div>

    <!-- Identificación CF (montos altos) -->
    <mat-card class="cardWithShadow m-b-16" *ngIf="!isViewMode && requiresConsumerIdentification">
      <mat-card-content class="p-t-8">
        <mat-form-field appearance="outline" class="w-100">
          <mat-label>DNI del consumidor final</mat-label>
          <input matInput [formControl]="receiverDocNumberControl" autocomplete="off" />
          <mat-hint>Requerido para montos iguales o superiores a ${{ consumerIdentificationThreshold | number:'1.0-0':'es-AR' }}</mat-hint>
        </mat-form-field>
      </mat-card-content>
    </mat-card>

    <!-- Totales -->
    <mat-card class="cardWithShadow m-b-16" *ngIf="!isViewMode">
      <mat-card-content class="p-t-8 d-flex justify-content-end">
        <app-fiscal-document-totals
          [subtotal]="invoiceForm.get('subtotal')?.value"
          [taxBreakdown]="invoiceTaxBreakdown"
          [taxTotal]="invoiceForm.get('taxTotal')?.value"
          [total]="invoiceForm.get('total')?.value"
          [taxModeNone]="isTaxModeNone()"
          [isViewMode]="isViewMode"
        ></app-fiscal-document-totals>
      </mat-card-content>
    </mat-card>
    <div class="invoice-view-section invoice-view-section--totals" *ngIf="isViewMode">
      <app-fiscal-document-totals
        [subtotal]="invoiceForm.get('subtotal')?.value"
        [taxBreakdown]="invoiceTaxBreakdown"
        [taxTotal]="invoiceForm.get('taxTotal')?.value"
        [total]="invoiceForm.get('total')?.value"
        [taxModeNone]="isTaxModeNone()"
        [isViewMode]="isViewMode"
      ></app-fiscal-document-totals>
    </div>

    <!-- Presentación fiscal (vista) -->
    <mat-expansion-panel
      class="invoice-view-fiscal-panel m-b-16"
      *ngIf="isViewMode && viewInvoiceFiscal && !isTaxModeNone() && (viewInvoiceFiscal.alicIvas?.length || viewInvoiceFiscal.tributos?.length)"
      [expanded]="false"
    >
      <mat-expansion-panel-header>
        <mat-panel-title class="f-s-14 f-w-600">
          Presentación fiscal ({{ viewInvoiceFiscal.tenantTaxMode }})
        </mat-panel-title>
      </mat-expansion-panel-header>
      <div class="invoice-view-fiscal-body">
        <div *ngIf="viewInvoiceFiscal.alicIvas?.length">
          <div class="f-s-13 f-w-600 m-b-8">Alicuotas IVA</div>
          <ul class="list-unstyled f-s-12 text-muted m-b-16 p-0">
            <li *ngFor="let a of viewInvoiceFiscal.alicIvas" class="m-b-4">
              Código {{ a.fiscalCode || '—' }} — Base ${{ a.baseAmount | number:'1.2-2' }} — Importe ${{ a.amount | number:'1.2-2' }}
            </li>
          </ul>
        </div>
        <div *ngIf="viewInvoiceFiscal.tributos?.length">
          <div class="f-s-13 f-w-600 m-b-8">Tributos</div>
          <ul class="list-unstyled f-s-12 text-muted m-0 p-0">
            <li *ngFor="let tr of viewInvoiceFiscal.tributos" class="m-b-4">
              {{ tr.fiscalCode || '—' }} ({{ tr.fiscalSystem || '—' }}) — Base ${{ tr.baseAmount | number:'1.2-2' }} — Importe ${{ tr.amount | number:'1.2-2' }}
            </li>
          </ul>
        </div>
      </div>
    </mat-expansion-panel>

    <!-- Vencimientos (vista, solo cuenta corriente) -->
    <div class="invoice-view-section invoice-view-section--due-dates" *ngIf="shouldShowDueDates()">
      <div class="invoice-view-section-title f-s-14 f-w-600 m-b-8">Vencimientos</div>
        <div *ngIf="loadingDueDates" class="text-center p-2">
          <mat-spinner diameter="24" class="inline-spinner"></mat-spinner>
        </div>

        <ng-container *ngIf="!loadingDueDates && invoiceDueDates">
          <!-- Alerta vencida -->
          <div *ngIf="invoiceDueDates.isOverdue" class="due-dates-overdue-alert">
            <mat-icon>warning</mat-icon>
            Factura vencida — se aplicó el nivel de recargo {{ invoiceDueDates.appliedSurchargeLevel }}
          </div>

          <table class="due-dates-table">
            <thead>
              <tr>
                <th>Tramo</th>
                <th>Fecha</th>
                <th class="text-end">Base</th>
                <th class="text-end">Recargo</th>
                <th class="text-end">Total</th>
                <th class="text-center">Estado</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let d of invoiceDueDates.dueDates"
                  [class.status-vigente]="d.status === 'vigente'"
                  [class.status-vencido]="d.status === 'vencido'">
                <td>{{ d.dueDateNumber }}</td>
                <td>{{ d.dueDate | date:'dd/MM/yyyy' }}</td>
                <td class="text-end">${{ d.baseAmount | number:'1.2-2' }}</td>
                <td class="text-end">
                  <span *ngIf="d.surchargePercent > 0">{{ d.surchargePercent }}%</span>
                  <span *ngIf="d.surchargeFixed > 0 && d.surchargePercent > 0"> + </span>
                  <span *ngIf="d.surchargeFixed > 0">${{ d.surchargeFixed | number:'1.2-2' }}</span>
                  <span *ngIf="d.surchargePercent === 0 && d.surchargeFixed === 0">—</span>
                </td>
                <td class="text-end due-date-total">${{ d.totalAmount | number:'1.2-2' }}</td>
                <td class="text-center">
                  <span class="due-date-status-badge"
                    [class.status-vigente]="d.status === 'vigente'"
                    [class.status-vencido]="d.status === 'vencido'"
                    [class.status-default]="d.status !== 'vigente' && d.status !== 'vencido'">
                    {{ d.status }}
                    <mat-icon *ngIf="d.triggersSuspension"
                      class="due-date-suspension-icon"
                      title="Genera suspensión si no se paga">block</mat-icon>
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </ng-container>

        <div *ngIf="!loadingDueDates && !invoiceDueDates" class="text-muted f-s-13 p-2">
          Sin información de vencimientos
        </div>
    </div>

  </form>
</mat-dialog-content>

<mat-dialog-actions align="end" class="manual-invoice-dialog-actions" *ngIf="!isViewMode">
  <button type="button" mat-stroked-button (click)="onCancel()" [disabled]="loading">Cancelar</button>
  <button
    type="button"
    mat-flat-button
    color="primary"
    class="m-l-8"
    (click)="onSubmit()"
    [disabled]="loading || invoiceForm.invalid || invoiceForm.get('total')?.value <= 0 || (requiresConsumerIdentification && receiverDocNumberControl.invalid)"
  >
    <mat-spinner diameter="20" *ngIf="loading" class="m-r-8 inline-spinner"></mat-spinner>
    <mat-icon *ngIf="!loading">save</mat-icon>
    <span>{{ loading ? 'Procesando…' : 'Emitir factura' }}</span>
  </button>
</mat-dialog-actions>

<mat-dialog-actions align="end" class="manual-invoice-dialog-actions" *ngIf="isViewMode">
  <button type="button" mat-stroked-button (click)="printDocument()" *ngIf="viewInvoiceFiscal">
    <mat-icon>print</mat-icon>
    Imprimir
  </button>
  <button
    type="button"
    mat-flat-button
    color="primary"
    (click)="onCancel()"
    *ngIf="!showCancelButton || data.invoice?.reversed"
  >
    Cerrar
  </button>
  <button type="button" mat-stroked-button (click)="onCancel()" *ngIf="showCancelButton && !data.invoice?.reversed">
    Cerrar
  </button>
  <button
    *ngIf="showCancelButton && !data.invoice?.reversed"
    type="button"
    mat-flat-button
    color="warn"
    class="m-l-8"
    (click)="cancelInvoice()"
    [disabled]="loading || data.invoice?.status === 'Canceled'"
  >
    <mat-spinner diameter="20" *ngIf="loading" class="m-r-8 inline-spinner"></mat-spinner>
    <mat-icon *ngIf="!loading">cancel</mat-icon>
    <span>{{ loading ? 'Revirtiendo…' : 'Revertir factura' }}</span>
  </button>
</mat-dialog-actions>
